import type { Matchday } from '@/api/types';
import { isPastDeadline } from './datetime';

/**
 * Chronologische Ordnung über deadline_at (Spieltage ohne Deadline ans Ende).
 * sort_order taugt NICHT als globale Ordnung: er ist nur je Wettbewerb eindeutig
 * (OpenLigaDB groupOrderID — Liga 1..38 überlappt Pokal 1..7).
 */
export function sortChronologically(matchdays: Matchday[]): Matchday[] {
  return [...matchdays].sort((a, b) => {
    const da = a.deadline_at ?? '9999-12-31';
    const db = b.deadline_at ?? '9999-12-31';
    return da.localeCompare(db) || a.id - b.id;
  });
}

/** Liga/Pokal bevorzugt (Bonus-Spieltage raus), sonst irgendein Spieltag. */
function relevantPool(matchdays: Matchday[]): Matchday[] {
  const sorted = sortChronologically(matchdays);
  const preferred = sorted.filter((md) => md.kind !== 'bonus');
  return preferred.length > 0 ? preferred : sorted;
}

/** Ab wie vielen Stunden vor Tippschluss die Tippübersicht schon auf den kommenden Spieltag zeigt. */
const OVERVIEW_LEAD_HOURS = 24;

/**
 * Ermittelt den Spieltag für die Tippübersicht (Direkteinstieg, Reiter-Wechsel):
 * zeigt den zuletzt entschiedenen Spieltag (Tippschluss vorbei) so lange weiter,
 * bis der Tippschluss des kommenden Spieltags in weniger als
 * OVERVIEW_LEAD_HOURS liegt — erst dann wird umgeschaltet.
 */
export function pickCurrentMatchday(matchdays: Matchday[]): Matchday | null {
  const pool = relevantPool(matchdays);

  const effectiveNow = new Date(Date.now() + OVERVIEW_LEAD_HOURS * 60 * 60 * 1000);
  const isDue = (md: Matchday) => isPastDeadline(md.deadline_at, effectiveNow);

  const decided = pool.filter(isDue);
  if (decided.length > 0) return decided[decided.length - 1] ?? null;

  return pool.find((md) => !isDue(md)) ?? pool[0] ?? null;
}

/**
 * Ermittelt den Spieltag für die Tippabgabe: springt direkt beim eigenen
 * Tippschluss zum nächsten Spieltag, unabhängig davon, wie weit dessen
 * Tippschluss noch entfernt ist (anders als pickCurrentMatchday, das den
 * letzten entschiedenen Spieltag noch etwas laenger zeigt).
 */
export function pickActiveMatchday(matchdays: Matchday[]): Matchday | null {
  const pool = relevantPool(matchdays);
  return pool.find((md) => !isPastDeadline(md.deadline_at)) ?? pool[pool.length - 1] ?? null;
}
